import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Button } from '@companycam/slab-web';
import { tokens } from '@companycam/slab-tokens';

import {
  COMPANY_INDUSTRY_UPDATE_MUTATION,
  INDUSTRIES_QUERY,
} from './graphql/queries.gql';
import CCRadioButton from 'components/shared/DesignSystem/SlabTransferPortal/CCRadioButton';
import Headline from '../signup/shared/Headline';
import toast from 'helpers/toast';
import _ from 'lodash';
import Steven from 'helpers/steven';

const SkipButton = styled(Button)`
  padding-bottom: ${tokens.size_spacing_s}px;
  justify-content: flex-end !important;
  padding-right: 0 !important;
`;

const RadioButtonWrapper = styled.div`
  min-height: 56px;
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  border-bottom: solid 1px var(--cc_color_border_default);
  display: flex;
  align-items: center;
  padding-left: var(--cc_size_spacing_s);
`;

const TradeInfoView = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 90vw;
  height: 100%;
  margin-top: -32px;
`;

const TradeOptionsWrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`;
const TradeOptionsListWrapper = styled.div`
  overflow: scroll;
  margin: auto 0;
  flex: 1;
  max-height: max(60vh, 400px);
`;
const TradeOptionsButtonWrapper = styled.div`
  padding-top: 16px;
`;
const StyledHeadline = styled(Headline)`
  font-size: 16px;
`;
const ContinueButton = styled(Button)`
  width: 100% !important;
`;

const messages = defineMessages({
  title: {
    defaultMessage: 'What do you do?',
    id: 'Onboard.signUp.industry.prompt',
    description: 'title for trade info',
  },
  skipButton: {
    defaultMessage: 'Skip',
    id: 'Onboard.signUp.industry.skip',
    description: 'skip button for trade info',
  },
});

const TradeInfo = ({ mobileDeeplinkHref }) => {
  // this is the event fired from mobile web
  useEffect(() => {
    Steven.signupStep({
      step_name: 'view_gather_trade',
    });
  }, []);

  const { data: queryData, loading: isLoading } = useQuery(INDUSTRIES_QUERY);
  const defaultIndustries = queryData?.defaultIndustries ?? [];
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const companyId = useSelector((state) => state.company.node_id);

  const onFinished = () => {
    window.Turbo.visit(mobileDeeplinkHref);
  };

  const [updateCompany] = useMutation(COMPANY_INDUSTRY_UPDATE_MUTATION, {
    onCompleted: (data) => {
      const gqlErrors = _.get(data, 'companyUpdate.errors', []);

      if (gqlErrors.length) {
        toast.error(gqlErrors[0].message);
      } else {
        onFinished();
      }
    },
    onError: (mutationError) => {
      toast.error(mutationError[0].message);
    },
  });
  const onSelectIndustry = () => {
    updateCompany({
      variables: {
        input: {
          id: companyId,
          company: {
            industry: { id: selectedIndustry },
          },
        },
      },
    });
  };
  const { formatMessage } = useIntl();
  const title = formatMessage(messages.title);

  const handleIndustryChange = (e) => {
    setSelectedIndustry(e.target.value);
  };

  if (isLoading) {
    return null;
  }

  return (
    <TradeInfoView>
      <TradeOptionsWrapper>
        <SkipButton color="secondary" design="minimal" onClick={onFinished}>
          {formatMessage(messages.skipButton)}
        </SkipButton>
        <div>
          <StyledHeadline text={title} />
        </div>
        <TradeOptionsListWrapper>
          {defaultIndustries.map((option) => (
            <RadioButtonWrapper key={option.id}>
              <CCRadioButton
                name="industry"
                id={option.id}
                label={option.name}
                value={option.id}
                onChange={handleIndustryChange}
                checked={option.id === selectedIndustry}
              />
            </RadioButtonWrapper>
          ))}
        </TradeOptionsListWrapper>
      </TradeOptionsWrapper>
      <TradeOptionsButtonWrapper>
        <ContinueButton
          color="primary"
          aria-label={formatMessage({ id: 'Common.done' })}
          onClick={onSelectIndustry}
          disabled={!selectedIndustry}
        >
          <FormattedMessage
            id="Onboard.signUp.button"
            description="Button text on sign up page"
            defaultMessage="Continue"
          />
        </ContinueButton>
      </TradeOptionsButtonWrapper>
    </TradeInfoView>
  );
};

export default TradeInfo;

TradeInfo.propTypes = {
  mobileDeeplinkHref: PropTypes.string,
};
